import { zodResolver } from '@hookform/resolvers/zod';
import {
  IGetSubscriptionSchema,
  ISubscriptionCancelSchema,
  SubscriptionCancelSchema,
} from '@monetize/types/app';
import { toDateOnly } from '@monetize/utils/core';
import { FunctionComponent as FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import { usePerformEntityAction } from '../../../../../api/queryUtils';
import {
  MButton,
  MCenterModal,
  MCheckbox,
  MFlex,
  MFormField,
  MStack,
  MText,
  MTooltip,
  MVStack,
} from '../../../../../components/Monetize';
import { DatePicker } from '../../../../../components/Monetize/DatePicker/DatePicker';
import { useToast } from '../../../../../services/toast';

/** A fresh "today" each time, so a modal left mounted overnight still starts on today. */
const getDefaultValues = (): ISubscriptionCancelSchema => ({
  effectiveDate: toDateOnly(new Date(), 'userTimezone'),
  prorate: false,
});

interface CancelSubscriptionProps {
  accountId: string;
  billGroupId: string;
  subscriptionId: string;
  isOpen: boolean;
  onClose: (redirect?: boolean) => void;
}

export const CancelSubscription: FC<CancelSubscriptionProps> = (
  props: CancelSubscriptionProps,
) => {
  const { isOpen, onClose, accountId, billGroupId, subscriptionId } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const { addToast } = useToast();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ISubscriptionCancelSchema>({
    resolver: zodResolver(SubscriptionCancelSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  // The subscriptions list shares one instance across rows and keeps it
  // mounted, so without this a date picked for one subscription carried over
  // to the next subscription opened.
  const handleClose = (redirect?: boolean) => {
    reset(getDefaultValues());
    onClose(redirect);
  };

  const { mutateAsync: doCancel } =
    usePerformEntityAction<IGetSubscriptionSchema>('accountSubscriptions', {
      endpointArgs: { accountId, billGroupId, isCancelOrActivate: true },
      meta: { showErrorToast: true },
    });

  const onSubmit = async (data: ISubscriptionCancelSchema) => {
    setLoading(true);
    try {
      await doCancel({ id: subscriptionId, action: 'cancel', data });
      addToast({
        summary: 'Subscription Canceled.',
        detail: `Subscription has successfully been canceled.`,
        severity: 'info',
      });
      handleClose();
    } catch (error) {
      handleApiErrorToast(error);
    }
    setLoading(false);
  };

  return (
    <MCenterModal
      size="sm"
      isOpen={isOpen}
      onClose={() => handleClose(false)}
      modalTitle="Cancel Subscription"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            variant="cancel"
            onClick={() => handleClose(false)}
            minW="auto"
          >
            Close
          </MButton>
          <MButton
            variant="delete"
            isLoading={loading}
            type="submit"
            onClick={handleSubmit(onSubmit)}
            minW="auto"
          >
            Cancel
          </MButton>
        </MStack>
      )}
    >
      <MFlex justifyContent="start">
        <form onSubmit={handleSubmit(onSubmit)}>
          <MVStack gap={2} alignItems="left" mt={-3}>
            <MText color="tGray.darkPurple" lineClamp={1} fontSize="sm">
              Please select the effective date of the cancelation
            </MText>
            <MFormField error={errors.effectiveDate} label="Effective Date">
              <Controller
                name="effectiveDate"
                control={control}
                render={({ field }) => (
                  <DatePicker {...field} isDisabled={loading} clearable />
                )}
              />
            </MFormField>
            <MFormField error={errors.prorate}>
              <Controller
                name="prorate"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MCheckbox
                    isChecked={value as any}
                    fontWeight="normal"
                    {...rest}
                  >
                    <MTooltip
                      placement="right"
                      label={
                        <MVStack gap="0px" alignContent="start">
                          <MText color="tWhite.base" fontSize={12}>
                            Choose this if you would like to credit
                          </MText>
                          <MText color="tWhite.base" fontSize={12}>
                            the customer for their unused period
                          </MText>
                        </MVStack>
                      }
                    >
                      Prorate & Credit
                    </MTooltip>
                  </MCheckbox>
                )}
              />
            </MFormField>
          </MVStack>
        </form>
      </MFlex>
    </MCenterModal>
  );
};
